import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Briefcase, Settings } from "lucide-react";
import partsToolsIcon from "@/assets/parts-tools-icon.png";
import { useAuth } from "@/components/AuthProvider";
import { EngineIcon } from "@/components/EngineIcon";
import { useFreeListingsStatus } from "@/hooks/useFreeListingsStatus";
import HeroBackground from "@/components/HeroBackground";
import chopperSwapLogo from "@/assets/chopperswap-logo-new.png";
import engineTurbineBase from "@/assets/engine-turbine-base.png";
import helicopterIcon from "@/assets/helicopter-silhouette-icon.png";
import MobileCarouselSelector, { CarouselItem } from "@/components/mobile/MobileCarouselSelector";

const EngineHeroSection = () => {
  const { user } = useAuth();
  const { isEnabled: isFreeMode } = useFreeListingsStatus();

  const scrollToCards = () => {
    document.getElementById('engine-service-cards')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative pt-[calc(var(--nav-height-mobile)_+_0.5rem)] lg:pt-0 pb-4 md:pb-0 md:min-h-screen md:flex md:items-center md:justify-center overflow-hidden dark:bg-background">
      {/* Hero Background - Contained to hero section only, no gap on mobile */}
      <HeroBackground />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 md:px-6 text-center">
        <div className="max-w-6xl mx-auto">
          {/* Mobile: Carousel Selector */}
          <div className="md:hidden mb-4 mt-0">
            <MobileCarouselSelector 
              items={[
                {
                  id: 'helicopter',
                  label: 'Helicopters',
                  fullLabel: 'Helicopter Marketplace',
                  icon: <img src={helicopterIcon} alt="" className="w-14 h-14 object-contain brightness-0 invert scale-y-150 -scale-x-150" />,
                  href: '/marketplace',
                },
                {
                  id: 'engine',
                  label: 'Engines',
                  fullLabel: 'Engine Marketplace',
                  icon: <img src={engineTurbineBase} alt="" className="w-10 h-10 object-contain brightness-0 invert" />,
                  href: '/marketplace?asset=engine',
                  isActive: true,
                },
                {
                  id: 'parts',
                  label: 'Parts & Tools',
                  fullLabel: 'Parts Marketplace',
                  icon: <img src={partsToolsIcon} alt="" className="w-14 h-14 object-contain brightness-0 invert scale-125" />,
                  href: '/marketplace?asset=parts',
                },
                {
                  id: 'jobs',
                  label: 'Jobs',
                  fullLabel: 'Jobs Marketplace',
                  icon: <Briefcase className="w-10 h-10 text-white" />,
                  href: '/marketplace?asset=jobs',
                },
                {
                  id: 'services',
                  label: 'Services',
                  fullLabel: 'Services Marketplace',
                  icon: <Settings className="w-10 h-10 text-white" />,
                  href: '/marketplace?asset=services',
                },
              ] as CarouselItem[]}
              startIndex={1}
              variant="marketplace"
            />
          </div>

          {/* Desktop/Tablet: Original Row Layout */}
          <div className="hidden md:flex flex-nowrap justify-center items-center gap-4 lg:gap-6 mb-6 md:mb-8 mt-4 lg:mt-[clamp(8rem,15vh,12rem)]">
            <Link 
              to="/marketplace" 
              className="group relative flex flex-col items-center justify-center 
                         w-[clamp(9rem,18vw,14rem)] h-[clamp(9rem,18vw,14rem)]
                         bg-white/35 backdrop-blur-xl 
                         border-2 border-white/50 rounded-2xl
                         hover:bg-white/45 hover:border-white/70 hover:scale-105
                         transition-all duration-300 
                         shadow-xl hover:shadow-2xl
                         dark:bg-card dark:border-border dark:hover:bg-card/80 dark:text-foreground dark:backdrop-blur-none dark:shadow-md"
            >
              <div className="h-[clamp(4rem,8vw,6rem)] flex items-center justify-center mb-2">
                <img src={helicopterIcon} alt="" className="w-[clamp(5rem,10vw,8rem)] h-[clamp(5rem,10vw,8rem)] object-contain brightness-0 invert scale-y-150 -scale-x-150" />
              </div>
              <span className="text-white dark:text-foreground font-semibold text-[clamp(0.75rem,1.4vw,1.1rem)] text-center px-2 leading-tight">
                Helicopter<br />Marketplace
              </span>
              <ArrowRight className="absolute bottom-3 right-3 w-5 h-5 text-white/60 dark:text-muted-foreground
                                     group-hover:translate-x-1 group-hover:text-white dark:group-hover:text-foreground transition-all" />
            </Link>
            
            <Link 
              to="/marketplace?asset=engine" 
              className="group relative flex flex-col items-center justify-center 
                         w-[clamp(9rem,18vw,14rem)] h-[clamp(9rem,18vw,14rem)]
                         bg-white/45 backdrop-blur-xl 
                         border-2 border-white/80 rounded-2xl
                         ring-2 ring-white/60 ring-offset-2 ring-offset-transparent
                         hover:bg-white/55 hover:border-white hover:scale-105
                         transition-all duration-300 
                         shadow-xl shadow-white/20 hover:shadow-2xl
                         dark:bg-card dark:border-primary dark:ring-primary/40 dark:text-foreground dark:backdrop-blur-none dark:shadow-md"
            >
              <div className="h-[clamp(4rem,8vw,6rem)] flex items-center justify-center mb-2">
                <img src={engineTurbineBase} alt="" className="w-[clamp(3rem,6vw,5rem)] h-[clamp(3rem,6vw,5rem)] object-contain brightness-0 invert" />
              </div>
              <span className="text-white dark:text-foreground font-semibold text-[clamp(0.75rem,1.4vw,1.1rem)] text-center px-2 leading-tight">
                Engine<br />Marketplace
              </span>
              <ArrowRight className="absolute bottom-3 right-3 w-5 h-5 text-white/60 dark:text-muted-foreground
                                     group-hover:translate-x-1 group-hover:text-white dark:group-hover:text-foreground transition-all" />
            </Link>
            
            <Link 
              to="/marketplace?asset=parts" 
              className="group relative flex flex-col items-center justify-center 
                         w-[clamp(9rem,18vw,14rem)] h-[clamp(9rem,18vw,14rem)]
                         bg-white/35 backdrop-blur-xl 
                         border-2 border-white/50 rounded-2xl
                         hover:bg-white/45 hover:border-white/70 hover:scale-105
                         transition-all duration-300 
                         shadow-xl hover:shadow-2xl
                         dark:bg-card dark:border-border dark:hover:bg-card/80 dark:text-foreground dark:backdrop-blur-none dark:shadow-md"
            >
              <div className="h-[clamp(4rem,8vw,6rem)] flex items-center justify-center mb-2">
                <img src={partsToolsIcon} alt="" className="w-[clamp(5rem,10vw,7rem)] h-[clamp(5rem,10vw,7rem)] object-contain brightness-0 invert" />
              </div>
              <span className="text-white dark:text-foreground font-semibold text-[clamp(0.75rem,1.4vw,1.1rem)] text-center px-2 leading-tight">
                Parts & Tools<br />Marketplace
              </span>
              <ArrowRight className="absolute bottom-3 right-3 w-5 h-5 text-white/60 dark:text-muted-foreground
                                     group-hover:translate-x-1 group-hover:text-white dark:group-hover:text-foreground transition-all" />
            </Link>
            
            <Link 
              to="/marketplace?asset=jobs" 
              className="group relative flex flex-col items-center justify-center 
                         w-[clamp(9rem,18vw,14rem)] h-[clamp(9rem,18vw,14rem)]
                         bg-white/35 backdrop-blur-xl 
                         border-2 border-white/50 rounded-2xl
                         hover:bg-white/45 hover:border-white/70 hover:scale-105
                         transition-all duration-300 
                         shadow-xl hover:shadow-2xl
                         dark:bg-card dark:border-border dark:hover:bg-card/80 dark:text-foreground dark:backdrop-blur-none dark:shadow-md"
            >
              <div className="h-[clamp(4rem,8vw,6rem)] flex items-center justify-center mb-2">
                <Briefcase className="w-[clamp(3rem,6vw,5rem)] h-[clamp(3rem,6vw,5rem)] text-white dark:text-foreground" />
              </div>
              <span className="text-white dark:text-foreground font-semibold text-[clamp(0.75rem,1.4vw,1.1rem)] text-center px-2 leading-tight">
                Jobs<br />Marketplace
              </span>
              <ArrowRight className="absolute bottom-3 right-3 w-5 h-5 text-white/60 dark:text-muted-foreground
                                     group-hover:translate-x-1 group-hover:text-white dark:group-hover:text-foreground transition-all" />
            </Link>

            <Link 
              to="/marketplace?asset=services"
              className="group relative flex flex-col items-center justify-center 
                         w-[clamp(9rem,18vw,14rem)] h-[clamp(9rem,18vw,14rem)]
                         bg-white/35 backdrop-blur-xl 
                         border-2 border-white/50 rounded-2xl
                         hover:bg-white/45 hover:border-white/70 hover:scale-105
                         transition-all duration-300 
                         shadow-xl hover:shadow-2xl
                         dark:bg-card dark:border-border dark:hover:bg-card/80 dark:text-foreground dark:backdrop-blur-none dark:shadow-md"
            >
              <div className="h-[clamp(4rem,8vw,6rem)] flex items-center justify-center mb-2">
                <Settings className="w-[clamp(3rem,6vw,5rem)] h-[clamp(3rem,6vw,5rem)] text-white dark:text-foreground" />
              </div>
              <span className="text-white dark:text-foreground font-semibold text-[clamp(0.75rem,1.4vw,1.1rem)] text-center px-2 leading-tight">
                Services<br />Marketplace
              </span>
              <ArrowRight className="absolute bottom-3 right-3 w-5 h-5 text-white/60 dark:text-muted-foreground
                                     group-hover:translate-x-1 group-hover:text-white dark:group-hover:text-foreground transition-all" />
            </Link>
          </div>

          {/* ChopperSwap Logo - Optimized for mobile */}
          <div className="mb-0 md:mb-8 mt-8 md:mt-6 relative">
            <img 
              src={chopperSwapLogo} 
              alt="ChopperSwap Logo" 
              className="h-16 md:h-32 lg:h-48 mx-auto mb-1 md:mb-4 drop-shadow-[0_0_30px_rgba(255,255,255,0.9)] brightness-0 invert"
            />
            {/* Animated Arrow Overlay - Hidden on mobile for performance */}
            <div className="hidden md:block absolute inset-0 h-32 lg:h-48 mx-auto mb-2 md:mb-4 animate-circular-flow opacity-60">
              <div className="relative w-full h-full">
                <div className="absolute top-1/4 left-1/2 w-2 h-2 bg-white rounded-full transform -translate-x-1/2 animate-pulse"></div>
                <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-white rounded-full animate-pulse delay-1000"></div>
                <div className="absolute bottom-1/4 left-1/2 w-2 h-2 bg-white rounded-full transform -translate-x-1/2 animate-pulse delay-2000"></div>
                <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-white rounded-full animate-pulse delay-3000"></div>
              </div>
            </div>
            {/* Rotor Animation Overlay - Smaller on mobile */}
            <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2 w-10 h-10 md:w-24 md:h-24 animate-rotor-spin opacity-40">
              <div className="absolute inset-0 border-t-2 border-white rounded-full"></div>
              <div className="absolute inset-0 border-b-2 border-white rounded-full rotate-90"></div>
            </div>
          </div>

          {/* Headline - Mobile optimized */}
          <h1 className="text-xl md:text-4xl lg:text-6xl font-bold text-white dark:text-foreground mb-1 md:mb-6 drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)] dark:drop-shadow-none leading-tight">
            Built by Professionals,
            <span className="block text-white dark:text-foreground">
              for Professionals
            </span>
          </h1>

          <p className="hidden md:block text-xs md:text-lg lg:text-xl text-white/95 dark:text-muted-foreground mb-2 md:mb-12 leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] dark:drop-shadow-none px-4 md:px-0">
            The community-driven engine network where every pro can list a turbine or module for sale or lease, post what they're sourcing, and connect directly with the operators, shops and suppliers they need — worldwide.
          </p>
          <p className="md:hidden text-xs text-white/95 dark:text-muted-foreground mb-2 leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] dark:drop-shadow-none px-4">
            The community-driven engine network. List, source and trade turbines — pro to pro.
          </p>



          {/* Engine Service Cards */}
          <div id="engine-service-cards" className="mb-0 md:mb-12 mx-auto w-full lg:w-[min(calc(2*23rem+1.5rem),calc(100vw-2rem))]">
            {/* Section Divider - Offering */}
            <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
              <div className="flex-1 h-[1.5px] bg-gradient-to-r from-transparent via-white/85 to-white/85 dark:via-white/60 dark:to-white/60"></div>
              <span className="text-white dark:text-white text-[10px] md:text-sm font-semibold tracking-[0.2em] uppercase drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] dark:drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">I'M OFFERING</span>
              <div className="flex-1 h-[1.5px] bg-gradient-to-l from-transparent via-white/85 to-white/85 dark:via-white/60 dark:to-white/60"></div>
            </div>
            
            {/* Mobile + Tablet: Carousel */}
            <div className="lg:hidden">
              <MobileCarouselSelector
                items={[
                  {
                    id: 'engine_sell',
                    label: 'Sell Engine',
                    icon: <EngineIcon engineType="engine_sell" className="w-full h-full text-white" density="card" />,
                    href: '/create-listing-engine?type=engine_sell',
                  },
                  {
                    id: 'engine_rent',
                    label: 'Rent Out Engine',
                    icon: <EngineIcon engineType="engine_rent" className="w-full h-full text-white" density="card" />,
                    href: '/create-listing-engine?type=engine_rent',
                  },
                ] as CarouselItem[]}
                startIndex={0}
                variant="action"
              />
            </div>


            {/* Desktop: Grid */}
            <div className="hidden lg:grid lg:grid-cols-2 gap-6 mb-6">
              <Link to="/create-listing-engine?type=engine_sell">
                <Card className="glass-card-dark group cursor-pointer h-full transition-shadow hover:!shadow-[0_30px_60px_-12px_rgba(0,0,0,0.65),0_0_0_1px_rgba(52,211,153,0.35)_inset,0_1px_0_rgba(255,255,255,0.25)_inset,0_0_40px_rgba(52,211,153,0.2)]">
                  <CardContent className="p-6 text-center flex flex-col h-full">
                    <EngineIcon engineType="engine_sell" className="w-44 h-44 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)] flex-shrink-0 text-white" />
                    <h3 className="text-lg font-semibold text-white mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">Sell Your Engine</h3>
                    <p className="text-sm text-white/85 mb-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">List your engine for sale with complete documentation</p>
                    <div className="mt-auto"><Button className="w-full">{isFreeMode ? 'List FREE' : 'List Engine'}</Button></div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/create-listing-engine?type=engine_rent">
                <Card className="glass-card-dark group cursor-pointer h-full transition-shadow hover:!shadow-[0_30px_60px_-12px_rgba(0,0,0,0.65),0_0_0_1px_rgba(52,211,153,0.35)_inset,0_1px_0_rgba(255,255,255,0.25)_inset,0_0_40px_rgba(52,211,153,0.2)]">
                  <CardContent className="p-6 text-center flex flex-col h-full">
                    <EngineIcon engineType="engine_rent" className="w-44 h-44 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)] flex-shrink-0 text-white" />
                    <h3 className="text-lg font-semibold text-white mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">Offer Engine Rental</h3>
                    <p className="text-sm text-white/85 mb-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">List your engine for rent and generate revenue</p>
                    <div className="mt-auto"><Button className="w-full">{isFreeMode ? 'Offer FREE' : 'Offer Rental'}</Button></div>
                  </CardContent>
                </Card>
              </Link>
            </div>

            {/* Section Divider - Looking For */}
            <div className="flex items-center gap-3 md:gap-4 mt-4 mb-3 md:mb-4">
              <div className="flex-1 h-[1.5px] bg-white/70 dark:bg-white/40"></div>
              <span className="text-white dark:text-white text-[10px] md:text-sm font-semibold tracking-[0.2em] uppercase drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] dark:drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">I'M LOOKING FOR</span>
              <div className="flex-1 h-[1.5px] bg-white/70 dark:bg-white/40"></div>
            </div>

            {/* Mobile + Tablet: Carousel */}
            <div className="lg:hidden">
              <MobileCarouselSelector
                items={[
                  {
                    id: 'engine_buy',
                    label: 'Buy Engine',
                    icon: <EngineIcon engineType="engine_buy" className="w-full h-full text-white" density="card" />,
                    href: '/create-listing-engine?type=engine_buy',
                  },
                  {
                    id: 'engine_rent_request',
                    label: 'Seek Rental',
                    icon: <EngineIcon engineType="engine_rent_request" className="w-full h-full text-white" density="card" />,
                    href: '/create-listing-engine?type=engine_rent_request',
                  },
                ] as CarouselItem[]}
                startIndex={0}
                variant="action"
              />
            </div>


            {/* Desktop: Grid */}
            <div className="hidden lg:grid lg:grid-cols-2 gap-6">
              <Link to="/create-listing-engine?type=engine_buy">
                <Card className="glass-card-dark group cursor-pointer h-full transition-shadow hover:!shadow-[0_30px_60px_-12px_rgba(0,0,0,0.65),0_0_0_1px_rgba(251,191,36,0.35)_inset,0_1px_0_rgba(255,255,255,0.25)_inset,0_0_40px_rgba(251,191,36,0.2)]">
                  <CardContent className="p-6 text-center flex flex-col h-full">
                    <EngineIcon engineType="engine_buy" className="w-44 h-44 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)] flex-shrink-0 text-white" />
                    <h3 className="text-lg font-semibold text-white mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">Looking to Buy</h3>
                    <p className="text-sm text-white/85 mb-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">Post a request to find engines for purchase</p>
                    <div className="mt-auto"><Button className="w-full">Post Buy Request</Button></div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/create-listing-engine?type=engine_rent_request">
                <Card className="glass-card-dark group cursor-pointer h-full transition-shadow hover:!shadow-[0_30px_60px_-12px_rgba(0,0,0,0.65),0_0_0_1px_rgba(251,191,36,0.35)_inset,0_1px_0_rgba(255,255,255,0.25)_inset,0_0_40px_rgba(251,191,36,0.2)]">
                  <CardContent className="p-6 text-center flex flex-col h-full">
                    <EngineIcon engineType="engine_rent_request" className="w-44 h-44 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)] flex-shrink-0 text-white" />
                    <h3 className="text-lg font-semibold text-white mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">Seeking Engine Rental</h3>
                    <p className="text-sm text-white/85 mb-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">Find engines available for rent</p>
                    <div className="mt-auto"><Button className="w-full">Find Rental</Button></div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Scroll Indicator - Hidden on mobile */}
      <div 
        className="hidden md:block absolute bottom-6 md:bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce cursor-pointer"
        onClick={scrollToCards}
      >
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default EngineHeroSection;
